/**
 * DevModePage - /devmode (cachee, pas dans le menu).
 *
 * Outils dev / troubleshoot pour le tenant courant :
 *  - Reset data (3 niveaux : audit, notifications, all)
 *  - Debug info : session, license, env vars, dernier scan
 *  - Bouton clear localStorage / cookies (frontend uniquement)
 *
 * Acces : admin uniquement (RBAC backend force aussi). Aucun lien depuis le
 * menu : on tape /devmode dans l URL ou on clique un lien depuis settings/about
 * (a ajouter plus tard si besoin).
 */
import { useState } from 'react';
import {
  Card, CardHeader, Title3, Body1, Caption1, Button, Spinner,
  MessageBar, MessageBarBody, MessageBarTitle,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions, DialogTrigger,
  tokens,
} from '@fluentui/react-components';
import {
  Beaker24Regular, Warning24Filled, Delete24Regular,
  ArrowSync24Regular, DocumentBulletList24Regular,
} from '@fluentui/react-icons';
import { PageHeader } from '../components/PageHeader';
import { useAuth } from '../auth/AuthContext';
import { useLicense } from '../auth/LicenseGate';
import { useActiveTenant } from '../hooks/useActiveTenant';
import { useLatestScan } from '../hooks/useScans';
import { devModeApi, type ResetScope, type ResetResponse } from '../api/devModeApi';
import { isDemoMode, setDemoMode } from '../api/liveMode';

const SCOPE_META: Record<ResetScope, { label: string; desc: string; danger: 'low' | 'mid' | 'high' }> = {
  audit:         { label: 'Audit',          desc: 'Supprime tous les scans + décisions manuelles. Conserve : licence, plan, tenant M365 lié, utilisateurs.', danger: 'mid' },
  notifications: { label: 'Notifications',  desc: 'Supprime règles d\'alertes + historique d\'alertes envoyées.', danger: 'low' },
  all:           { label: 'Tout (sauf binding)', desc: 'Combine audit + notifications + reset scope/schedule/customization. Conserve : licence, plan, m365TenantId, consentedAt, utilisateurs.', danger: 'high' },
};

export function DevModePage() {
  const { user }   = useAuth();
  const license    = useLicense();
  const { tenant } = useActiveTenant();
  const latestScan = useLatestScan(tenant.id);

  const [confirmingScope, setConfirmingScope] = useState<ResetScope | null>(null);
  const [resetting, setResetting]             = useState(false);
  const [result, setResult]                   = useState<ResetResponse | null>(null);
  const [error, setError]                     = useState<string | null>(null);

  const isAdmin = user?.role === 'admin';
  const demoActive = isDemoMode();

  function toggleDemo() {
    setDemoMode(!demoActive);
    window.location.reload();
  }

  async function runReset(scope: ResetScope) {
    setResetting(true);
    setError(null);
    try {
      const r = await devModeApi.reset(scope);
      setResult(r);
      setConfirmingScope(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setResetting(false);
    }
  }

  function clearLocalState() {
    try {
      localStorage.clear();
      sessionStorage.clear();
      alert('localStorage + sessionStorage vidés. Rechargez la page.');
    } catch (e) {
      alert(`Erreur : ${(e as Error).message}`);
    }
  }

  return (
    <div>
      <PageHeader
        title="DevMode — outils de troubleshoot"
        description="Page cachée pour les admins. Reset des données, debug info, helpers."
      />

      <MessageBar intent="warning" style={{ marginBottom: 16 }}>
        <MessageBarBody>
          <MessageBarTitle>⚠️ Outils sensibles</MessageBarTitle>
          Les actions ci-dessous sont destructives et limitées au tenant courant ({tenant.id}). Aucun rollback possible.
        </MessageBarBody>
      </MessageBar>

      {error && (
        <MessageBar intent="error" style={{ marginBottom: 16 }}>
          <MessageBarBody>
            <MessageBarTitle>Erreur</MessageBarTitle>
            {error}
          </MessageBarBody>
        </MessageBar>
      )}

      {result && (
        <MessageBar intent="success" style={{ marginBottom: 16 }}>
          <MessageBarBody>
            <MessageBarTitle>Reset effectué ({result.stats.scope})</MessageBarTitle>
            {result.message}
            <div style={{ marginTop: 8, fontSize: 12, color: tokens.colorNeutralForeground3 }}>
              Par {result.performedBy} en {result.elapsedMs} ms — {result.performedAt}
            </div>
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Mode démo */}
      <Card style={{ marginBottom: 16, borderLeft: `4px solid ${demoActive ? tokens.colorPaletteGreenBorder2 : tokens.colorBrandStroke1}` }}>
        <CardHeader
          image={<Beaker24Regular primaryFill={tokens.colorBrandForeground1} />}
          header={<Title3>Mode démo {demoActive ? '— ACTIF' : ''}</Title3>}
        />
        <Body1 block style={{ marginBottom: 12 }}>
          Affiche un faux client <strong>« Contoso Industries »</strong> en plan <strong>Business</strong> avec des
          données complètes (dashboard, Contrôles M365, Scan Azure 67 contrôles, Rapport CyFun). Aucune donnée réelle
          n'est appelée tant que le mode démo est actif. Idéal pour les démos et captures d'écran.
        </Body1>
        {demoActive ? (
          <MessageBar intent="success" style={{ marginBottom: 12 }}>
            <MessageBarBody>
              <MessageBarTitle>Mode démo actif</MessageBarTitle>
              L'application affiche des données fictives. Désactivez pour revenir aux données réelles.
            </MessageBarBody>
          </MessageBar>
        ) : (
          <MessageBar intent="info" style={{ marginBottom: 12 }}>
            <MessageBarBody>
              L'activation recharge l'application et bascule toutes les vues sur des données de démonstration.
            </MessageBarBody>
          </MessageBar>
        )}
        <Button
          appearance={demoActive ? 'outline' : 'primary'}
          icon={<Beaker24Regular />}
          onClick={toggleDemo}
        >
          {demoActive ? 'Désactiver le mode démo' : 'Activer le mode démo'}
        </Button>
      </Card>

      {/* Reset section */}
      <Card style={{ marginBottom: 16 }}>
        <CardHeader
          image={<Delete24Regular primaryFill={tokens.colorPaletteRedForeground1} />}
          header={<Title3>Reset des données</Title3>}
        />
        <Body1 block style={{ marginBottom: 16 }}>
          Choisissez le périmètre. La licence, le plan, les utilisateurs et le binding M365 sont toujours préservés.
        </Body1>

        {!isAdmin && (
          <MessageBar intent="info">
            <MessageBarBody>Seul un admin peut lancer un reset (rôle actuel : <strong>{user?.role ?? '?'}</strong>).</MessageBarBody>
          </MessageBar>
        )}

        {isAdmin && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {(Object.keys(SCOPE_META) as ResetScope[]).map((scope) => {
              const meta = SCOPE_META[scope];
              const color = meta.danger === 'high' ? tokens.colorPaletteRedBorder2
                        : meta.danger === 'mid'    ? tokens.colorPaletteDarkOrangeBorder2
                                                   : tokens.colorNeutralStroke1;
              return (
                <div key={scope} style={{
                  padding: 12, borderLeft: `4px solid ${color}`,
                  background: tokens.colorNeutralBackground2, borderRadius: 6,
                  display: 'flex', gap: 12, alignItems: 'center',
                }}>
                  <div style={{ flex: 1 }}>
                    <Body1 style={{ fontWeight: 600, display: 'block', marginBottom: 2 }}>{meta.label}</Body1>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{meta.desc}</Caption1>
                  </div>
                  <Button
                    appearance={meta.danger === 'high' ? 'primary' : 'outline'}
                    style={meta.danger === 'high' ? { background: tokens.colorPaletteRedBackground3, borderColor: tokens.colorPaletteRedBorder2 } : {}}
                    onClick={() => setConfirmingScope(scope)}
                    disabled={resetting}
                  >
                    Lancer
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Debug info */}
      <Card style={{ marginBottom: 16 }}>
        <CardHeader
          image={<DocumentBulletList24Regular primaryFill={tokens.colorBrandForeground1} />}
          header={<Title3>Debug info</Title3>}
        />
        <div style={{
          fontFamily: 'monospace', fontSize: 12,
          background: tokens.colorNeutralBackground2, padding: 16, borderRadius: 6,
          whiteSpace: 'pre-wrap', overflowX: 'auto',
        }}>
          {JSON.stringify({
            user: {
              id:    user?.id,
              email: user?.email,
              role:  user?.role,
              clientId: user?.clientId,
              name:  user?.name,
            },
            tenant: {
              id:           tenant.id,
              name:         tenant.name,
              domain:       tenant.domain,
              industry:     tenant.industry,
              size:         tenant.size,
            },
            license: {
              hasLicense:    license.hasLicense,
              isTrial:       license.isTrial,
              isExpired:     license.isExpired,
              plan:          license.status?.plan,
              mode:          license.status?.mode,
              valid:         license.status?.valid,
              validUntil:    license.status?.validUntil,
              daysRemaining: license.status?.daysRemaining,
            },
            latestScan: latestScan ? {
              id:           latestScan.id,
              startedAt:    latestScan.startedAt,
              finishedAt:   latestScan.finishedAt,
              globalScore:  latestScan.globalScore,
              resultsCount: latestScan.results?.length ?? 0,
            } : null,
            env: {
              VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
              VITE_REQUIRE_AUTH: import.meta.env.VITE_REQUIRE_AUTH,
              VITE_MARKETING_URL: import.meta.env.VITE_MARKETING_URL,
              MODE:              import.meta.env.MODE,
              DEV:               import.meta.env.DEV,
            },
            browser: {
              userAgent: navigator.userAgent,
              language:  navigator.language,
              now:       new Date().toISOString(),
            },
          }, null, 2)}
        </div>
      </Card>

      {/* Frontend helpers */}
      <Card>
        <CardHeader
          image={<Beaker24Regular primaryFill={tokens.colorBrandForeground1} />}
          header={<Title3>Helpers frontend</Title3>}
        />
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Button
            appearance="outline"
            icon={<ArrowSync24Regular />}
            onClick={() => license.refresh()}
          >
            Refresh license context
          </Button>
          <Button
            appearance="outline"
            icon={<Delete24Regular />}
            onClick={clearLocalState}
          >
            Clear localStorage + sessionStorage
          </Button>
          <Button
            appearance="outline"
            icon={<ArrowSync24Regular />}
            onClick={() => window.location.reload()}
          >
            Hard reload
          </Button>
        </div>
      </Card>

      {/* Confirm dialog */}
      {confirmingScope && (
        <Dialog open={true} onOpenChange={(_, d) => { if (!d.open) setConfirmingScope(null); }}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>
                <Warning24Filled style={{ color: tokens.colorPaletteRedForeground1, verticalAlign: '-4px', marginRight: 8 }}/>
                Confirmer le reset : {SCOPE_META[confirmingScope].label}
              </DialogTitle>
              <DialogContent>
                <Body1 block style={{ marginBottom: 12 }}>{SCOPE_META[confirmingScope].desc}</Body1>
                <MessageBar intent="warning">
                  <MessageBarBody>
                    Cette action est <strong>irréversible</strong> et impacte uniquement le tenant <code>{tenant.id}</code>.
                  </MessageBarBody>
                </MessageBar>
              </DialogContent>
              <DialogActions>
                <DialogTrigger disableButtonEnhancement>
                  <Button appearance="secondary" disabled={resetting}>Annuler</Button>
                </DialogTrigger>
                <Button
                  appearance="primary"
                  style={{ background: tokens.colorPaletteRedBackground3, borderColor: tokens.colorPaletteRedBorder2 }}
                  onClick={() => runReset(confirmingScope)}
                  disabled={resetting}
                  icon={resetting ? <Spinner size="tiny" /> : undefined}
                >
                  {resetting ? 'Reset en cours…' : 'Confirmer le reset'}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </div>
  );
}
